/**
 * Current survey (वर्तमान सर्वे, phone / ground): what share of the booth is strong / neutral / needs
 * work. Saved through the offline outbox like every section; the dashboard's sentiment chart and
 * the "strong / neutral / needs work" booth counts come from it. The phone never shows a saved
 * survey (counts only, 28 Oct 2026): it says the survey is done and offers a new one, which replaces it.
 */
import { useEffect, useState } from "react";
import type { SentimentData } from "@npac/rules";
import type { FormBase } from "@core/app";
import { localizeError } from "../i18n";
import { useApp, useQuery, useSession } from "../ui/context";
import { navigate } from "../ui/router";
import { Banner, Button, Choice, Field, NumberField } from "../ui/components";

type Method = NonNullable<SentimentData["method"]>;
const today = () => new Date(Date.now() + 5.5 * 3600_000).toISOString().slice(0, 10);

export function SentimentScreen({ code }: { code: string }) {
  const { app, t, lang } = useApp();
  const { assemblyId } = useSession();
  const [f, setF] = useState<{ strong: string; neutral: string; weak: string; sample: string; method: Method | ""; date: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const [fresh, setFresh] = useState(false);

  useEffect(() => {
    void app.core.loadBooth(code);
  }, [app, code]);

  const d = useQuery(
    async () => {
      if (!assemblyId) return null;
      const [booth, item, server] = await Promise.all([
        app.core.store.getBooth(assemblyId, code),
        app.core.store.findSectionItem(assemblyId, code, "sentiment"),
        app.core.store.getServerSection(assemblyId, code, "sentiment"),
      ]);
      return { booth, item, server };
    },
    [assemblyId, code],
    ["booths", "sections", "outbox"]
  );

  useEffect(() => {
    if (!d || f) return;
    // only the surveyor's own unsent survey is shown again; a saved one never comes to the phone
    const s = d.item?.payload.data as SentimentData | undefined;
    setF({
      strong: s ? String(s.strong) : "",
      neutral: s ? String(s.neutral) : "",
      weak: s ? String(s.weak) : "",
      sample: s?.sampleSize != null ? String(s.sampleSize) : "",
      method: s?.method ?? "",
      date: s?.surveyedOn ?? today(),
    });
  }, [d, f]);

  if (!d || !f) return null;
  if (!d.booth) return <Banner kind="error">{t.boothUnknown(code)}</Banner>;
  const editable = app.canEdit(d.booth) && app.can("sentiment");
  const num = (v: string) => (v.trim() === "" ? NaN : Number(v.replace(",", ".")));
  const sum = [f.strong, f.neutral, f.weak].map(num).reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0);
  const sumOk = Math.abs(sum - 100) <= 1;
  const base: FormBase = d.item
    ? { baseVersion: d.item.baseVersion, basedOnItemId: d.item.id }
    : { baseVersion: d.server?.version ?? d.booth.sections?.sentiment?.version ?? 0, basedOnItemId: null };

  const done = !d.item && !!(d.server?.counts?.survey || d.booth.sections?.sentiment);
  if (done && !fresh) {
    return (
      <div className="stack" data-testid="sentiment">
        <div>
          <div style={{ fontWeight: 800, color: "var(--primary)" }}>
            {t.boothNo} {d.booth.code} · {d.booth.name}
          </div>
          <h2 style={{ margin: "2px 0 4px" }}>{t.secSentiment}</h2>
        </div>
        <Banner kind="ok">{t.aoSurveyDone}</Banner>
        <Banner kind="info">
          {t.secSentimentHint}
          <div style={{ marginTop: 8 }}>
            <Button
              onClick={() => {
                void app.rememberBooth(d.booth?.code ?? "").then(() => navigate({ name: "survey" }));
              }}
              testId="sentiment-to-survey"
            >
              {t.svOpenPerson}
            </Button>
          </div>
        </Banner>
        <p className="muted ao-note">{t.aoNote}</p>
        {editable ? (
          <>
            <Button big onClick={() => setFresh(true)} testId="sentiment-new">
              {t.aoSurveyNew}
            </Button>
            <p className="muted" style={{ marginTop: -4 }}>
              {t.aoSurveyNewNote}
            </p>
          </>
        ) : null}
      </div>
    );
  }

  const save = async () => {
    if (!sumOk) return setMsg({ kind: "error", text: t.svSumBad });
    setBusy(true);
    setMsg(null);
    try {
      const raw: Record<string, unknown> = { strong: num(f.strong), neutral: num(f.neutral), weak: num(f.weak), surveyedOn: f.date || undefined };
      if (f.sample.trim()) raw.sampleSize = Number(f.sample);
      if (f.method) raw.method = f.method;
      const r = await app.core.saveSectionData(code, "sentiment", raw, base);
      if (!r.ok) return setMsg({ kind: "error", text: r.errors.map((x) => localizeError(x.message, lang)).join(" · ") });
      setMsg({ kind: "ok", text: r.synced ? t.pSavedOnline : t.pSavedOffline });
      setTimeout(() => navigate({ name: "booth", code }), 900);
    } finally {
      setBusy(false);
    }
  };
  const set = (k: keyof typeof f) => (v: string) => setF((x) => (x ? { ...x, [k]: v } : x));

  return (
    <form
      className="stack"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
      data-testid="sentiment"
    >
      <div>
        <div style={{ fontWeight: 800, color: "var(--primary)" }}>
          {t.boothNo} {d.booth.code} · {d.booth.name}
        </div>
        <h2 style={{ margin: "2px 0 4px" }}>{t.secSentiment}</h2>
        <p className="muted" style={{ margin: 0 }}>
          {t.svExplain}
        </p>
      </div>
      <Banner kind="info">
        {t.secSentimentHint}
        <div style={{ marginTop: 8 }}>
          <Button
              onClick={() => {
                void app.rememberBooth(d.booth?.code ?? "").then(() => navigate({ name: "survey" }));
              }}
              testId="sentiment-to-survey"
            >
            {t.svOpenPerson}
          </Button>
        </div>
      </Banner>
      {!editable ? <Banner kind="warn">{t.notAllowedEdit}</Banner> : null}
      <div className="sv-grid">
        <NumberField label={t.svStrong} name="sv-strong" value={f.strong} onChange={set("strong")} maxLength={5} className="sv-strong" />
        <NumberField label={t.svNeutral} name="sv-neutral" value={f.neutral} onChange={set("neutral")} maxLength={5} className="sv-neutral" />
        <NumberField label={t.svWeak} name="sv-weak" value={f.weak} onChange={set("weak")} maxLength={5} className="sv-weak" />
      </div>
      <div className={`sv-bar${sumOk ? "" : " bad"}`} aria-hidden>
        <i style={{ width: `${Math.max(0, num(f.strong) || 0)}%`, background: "var(--ok)" }} />
        <i style={{ width: `${Math.max(0, num(f.neutral) || 0)}%`, background: "#c98a00" }} />
        <i style={{ width: `${Math.max(0, num(f.weak) || 0)}%`, background: "var(--danger)" }} />
      </div>
      <div className={sumOk ? "ok-text" : "warn-text"} role="status">
        {t.svSum(Math.round(sum * 10) / 10)} {sumOk ? "✓" : `— ${t.svSumBad}`}
      </div>
      <div className="field">
        <span className="label">{t.svMethod}</span>
        <Choice<Method> name={t.svMethod} value={f.method} onChange={(v) => set("method")(v)} options={(["call", "ground", "mixed"] as Method[]).map((m) => ({ value: m, label: t[`m_${m}` as const] }))} />
      </div>
      <NumberField label={t.svSample} name="sv-sample" value={f.sample} onChange={set("sample")} maxLength={4} />
      <Field label={t.svDate} name="sv-date" type="date" value={f.date} onChange={set("date")} />
      {msg ? <Banner kind={msg.kind}>{msg.text}</Banner> : null}
      {editable ? (
        <Button type="submit" big busy={busy} disabled={!sumOk} testId="sentiment-save">
          {t.svSave}
        </Button>
      ) : null}
    </form>
  );
}
